/**
 * 학생 상태를 바꾸는 창(설계 §5 · §4-2) — 퇴원 · 퇴원일 변경 · 퇴원 예정 취소 · 휴원 · 복귀 · 등록/재등록 · 입반 · 반 이동 · 퇴반 ·
 * 학교·학년 변경 · 부서 변경 · 기간 정정 · 기간 삭제 · 학생 정보 수정. 서버 경로는 요청서 §7-5(S5~S17) · 백엔드 2단계 전달 §1(S4).
 *
 * 퇴원일·퇴반일은 마지막 수강일(last_day), 이동일·복귀일은 새 상태의 첫날로 받는다. 칸 아래에 「반에 있는 마지막 날」과
 * 시간표상 마지막 수업을 바로 보인다. 상세의 카드·작업 메뉴·반 편성 줄이 이 창을 연다.
 * 휴원·퇴원은 5단계에 열려 이 창의 작업은 모두 서버에 있다(백엔드 5단계 전달 §4-A).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  cancelWithdraw, changeDepartment, changeSchool, correctPeriod, deletePeriod, endEnrollment, enrollStudent, registerStudent,
  transferStudent, updateProfile, withdrawStudent, listDepartments, getStudent,
} from '../academyApi';
import { addDays, enrollmentLastDay, formatMonthDay, isActiveOn, isoWeekday, lastClassDay, lastDayOf, tenureLastDay, todayKST, weekdayLabel, withdrawalDay } from '../periodDates';
import { notifyAcademyChanged, SegTabs, useAsync } from '../ui';
import { PauseForm, ResumeForm } from './PauseResumeForms';
import { activePause, pauseEnrollmentPlan } from './pauseWorkflow';
import TestStudentField from './TestStudentField';
import { checkboxClass } from '../controls';
import {
  DateInput, EmptyCodesNote, Field, FormModal, MultiSelectInput, NAME_RULE, Note, PASSWORD_RULE, PasswordInput, SelectInput, StudentNoInput,
  TextArea, TextInput, gradeOptionsForSchool, schoolOptions, studentNoHint, studentNoOk, useClassList, useCodeList, useSchools,
} from './form';

export const ACTION_LABEL = {
  withdraw: '퇴원',
  changeWithdraw: '퇴원일 변경',
  cancelWithdraw: '퇴원 예정 취소',
  pause: '휴원',
  pauseClasses: '휴원 반 정리',
  resume: '복귀',
  register: '재등록',
  registerFirst: '등록',
  enroll: '입반(반 추가)',
  transfer: '반 이동',
  endEnrollment: '퇴반',
  school: '학교·학년 변경',
  department: '부서 변경',
  correctTenure: '재원 기간 정정',
  deleteTenure: '재원 기간 삭제',
  correctPause: '휴원 기간 정정',
  deletePause: '휴원 기간 삭제',
  correct: '정정',
  delete: '삭제',
  profile: '정보 수정',
};

const md = (d) => (d ? `${formatMonthDay(d)}(${weekdayLabel(isoWeekday(d))})` : '');
/** 사유는 묶음마다 「기타」 하나가 있다 — 고르면 자세한 내용은 메모로 적는다(오너 결정 2026-10-12, 백엔드 1단계 전달 §2) */

/**
 * 「상태」 카드가 여는 작업 — 지금 상태에 맞는 것만(맞지 않는 것은 서버도 409 invalid_state, 요청서 V23).
 * 재원 기간 삭제는 잘못 고른 계정에 [등록]한 것을 되돌리는 유일한 길이다(백엔드 2단계 전달 §4-H) — 정정 옆에 둔다.
 * 휴원 기간 삭제도 같다 — 잘못 넣은 휴원 · 퇴원일 당일 · 뒤에 시작해 퇴원을 막는 휴원을 지운다(백엔드 5단계 전달 §3).
 */
export function stateActions(detail) {
  const c = detail.current;
  const wd = c.withdraw_scheduled_on;
  const list = [];
  if (c.state === 'enrolled') list.push(...(wd ? ['changeWithdraw', 'cancelWithdraw', 'pause'] : ['pause', 'withdraw']));
  else if (c.state === 'paused') {
    list.push('resume', ...(wd ? ['changeWithdraw', 'cancelWithdraw'] : ['withdraw']));
    const pause = activePause(detail);
    if (pause && pauseEnrollmentPlan(detail, pause.start_on).length) list.push('pauseClasses');
  }
  else if (c.state === 'withdrawn') list.push('register');
  else if (c.state === 'none') list.push('registerFirst');
  if (detail.tenures.length) list.push('correctTenure', 'deleteTenure');
  if (detail.pauses.length) list.push('correctPause', 'deletePause');
  return list;
}
/**
 * [반 이동] · [퇴반]을 할 수 있는 지금 반 — 진행 중이거나 퇴원 예정으로만 닫힌 편성. 앞으로 퇴반 · 이동이 잡힌 편성은
 * 서버가 409 invalid_state 로 거절한다(날짜는 정정으로, 백엔드 4단계 전달 §3 · §4-C)
 */
export const movableClasses = (detail) => detail.current.classes
  .filter((c) => !c.end_on || detail.enrollments.some((e) => e.id === c.enrollment_id && e.auto_closed));

/** 「지금 반」 카드가 여는 작업 */
export function classActions(detail) {
  const active = ['enrolled', 'paused', 'register_scheduled'].includes(detail.current.state);
  if (!active) return [];
  return ['enroll', ...(movableClasses(detail).length ? ['transfer', 'endEnrollment'] : [])];
}

// ── 각 작업의 칸 ──

/** enabled 가 거짓이면 반 목록을 부르지 않는다 — 반을 쓰지 않는 창(학교 · 부서 · 재원 기간 정정)에서 */
function useClassMaps(enabled = true) {
  const all = useClassList('all', 0, enabled);
  const byId = useMemo(() => new Map(all.map((c) => [c.class_id, c])), [all]);
  // 고를 수 있는 반은 활성 반만 — 종강일이 없다(백엔드 3단계 전달 §4-G)
  return { all, byId, open: all.filter((c) => c.active) };
}

/** 그 반의 마지막 날 · 시간표상 마지막 수업 — 퇴원·반 이동·퇴반 창의 칸 아래 */
function lastLine(enrollment, endOn, byId) {
  if (!endOn) return null;
  const slots = byId.get(enrollment.class_id)?.slots || [];
  const lastClass = lastClassDay({ start_on: enrollment.start_on, end_on: endOn }, slots);
  return `${enrollment.class_name} — 반에 있는 마지막 날 ${md(lastDayOf(endOn))}${lastClass ? ` · 시간표상 마지막 수업 ${md(lastClass)}` : ' · 그 사이 수업 없음'}`;
}

/** 지금 재원 기간(상세 tenures 의 줄) — [퇴원일 변경]이 지금 사유 · 메모를 읽는다 */
const currentTenure = (detail) => detail.tenures.find((t) => t.id != null && t.id === detail.current.tenure?.id)
  || detail.tenures.find((t) => t.end_on && t.end_on === detail.current.withdraw_scheduled_on) || null;

/** 상세의 편성·휴원에는 tenure_id 가 없으므로 다음 재등록 시작일을 경계로 현재 재원에 속한 기록만 고른다. */
function withdrawalRecords(detail, tenure) {
  if (!tenure) return [];
  const nextStart = detail.tenures.filter((t) => t.start_on > tenure.start_on).map((t) => t.start_on).sort()[0];
  return [
    ...detail.enrollments.map((r) => ({ ...r, kind: 'enrollment', label: r.class_name || '반 편성' })),
    ...detail.pauses.map((r) => ({ ...r, kind: 'pause', label: '휴원' })),
  ].filter((r) => r.start_on >= tenure.start_on && (!nextStart || r.start_on < nextStart))
    // 반 이동은 가장 뒤의 기록부터 취소해야 앞 반의 연결이 보존된다. 매 단계 서버에서 다시 받는다.
    .sort((a, b) => b.start_on.localeCompare(a.start_on) || b.id - a.id);
}

function WithdrawForm({ detail, change, ...modal }) {
  const { getAccessToken } = useAuth();
  const reasons = useCodeList('withdraw_reason');
  const { byId } = useClassMaps();
  // [퇴원일 변경]은 지금 사유 · 메모를 미리 채운다 — 서버는 memo 를 보내면 그 값으로 바꾸므로, 비워 보내면 「기타」 내용이 지워진다(백엔드 5단계 전달 §4-F)
  const scheduled = change ? currentTenure(detail) : null;
  const [lastDay, setLastDay] = useState(change ? withdrawalDay(detail.current) : '');
  const [reason, setReason] = useState(scheduled?.withdraw_reason_code || '');
  const [memo, setMemo] = useState(scheduled?.withdraw_memo || '');
  const [freshDetail, setFreshDetail] = useState(null);
  const [initialTenure] = useState(() => currentTenure(detail));
  const [cancelReason, setCancelReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const changedRef = useRef(false);
  useEffect(() => () => {
    // 상태 창에서 다른 작업 탭으로 이동해도 이미 끝난 취소를 바깥 화면에 반영한다.
    if (changedRef.current) notifyAcademyChanged();
  }, []);
  const current = freshDetail && freshDetail.revision >= detail.revision ? freshDetail : detail;
  const tenure = currentTenure(current);
  const sameTenure = !!tenure && tenure.id === initialTenure?.id;
  const canWithdraw = sameTenure && ['enrolled', 'paused'].includes(current.current.state);
  const records = withdrawalRecords(current, tenure);
  const blocking = lastDay ? records.filter((r) => r.start_on > lastDay) : [];
  const target = blocking[0];
  const cancelling = !!target;
  const today = todayKST();
  // 고를 수 있는 사유여야 한다 — 미리 채운 사유가 그 뒤 감춰졌으면 고르기가 빈 칸으로 보이니 다시 고르게 한다(서버도 감춘 사유는 400)
  const reasonOk = reasons.items.some((r) => r.code === reason);
  const cancelLabel = target?.kind === 'pause' ? '휴원 기록 취소' : '입반 취소 후 계속';
  const submitLabel = refreshNeeded ? '학생 정보 다시 불러오기'
    : cancelling ? cancelLabel : lastDay >= today ? '퇴원 예정 저장' : '퇴원 저장';
  const reload = async () => {
    const latest = await getStudent(detail.student_id, {}, getAccessToken);
    setFreshDetail(latest);
    setRefreshNeeded(false);
    setConfirmed(false);
  };
  const close = () => {
    // 단계 도중 닫거나 요청 결과를 못 받은 경우에도 바깥 목록·상세를 갱신한다.
    if (changedRef.current) {
      changedRef.current = false;
      notifyAcademyChanged();
    }
    modal.onClose();
  };
  const submit = async () => {
    if (refreshNeeded) { await reload(); return false; }
    changedRef.current = true;
    try {
      if (cancelling) {
        await deletePeriod(target.kind, target.id, { reason: cancelReason.trim(), ifRevision: current.revision }, getAccessToken);
        setCompleted((items) => [...items, `${target.label} · ${target.start_on} 시작 기록 취소`]);
        setConfirmed(false);
        setCancelReason('');
        setRefreshNeeded(true);
        await reload();
        // 퇴원은 다음 버튼으로 따로 저장한다. 이미 취소한 기록을 다시 삭제하지 않는다.
        return false;
      }
      await withdrawStudent(detail.student_id, { lastDay, reasonCode: reason, memo, ifRevision: current.revision }, getAccessToken);
      changedRef.current = false;
      return true;
    } catch (error) {
      // 삭제/퇴원 응답이 유실되어도 같은 요청을 바로 재전송하지 않는다.
      setRefreshNeeded(true);
      setConfirmed(false);
      throw error;
    }
  };
  const pickDate = (value) => {
    setLastDay(value);
    setConfirmed(false);
    setCancelReason('');
  };
  return (
    <FormModal
      {...modal}
      onClose={close}
      lockWhileBusy
      danger
      cancelLabel={completed.length ? '닫기' : '취소'}
      submitLabel={submitLabel}
      canSubmit={refreshNeeded || (canWithdraw && !!lastDay && lastDay >= (tenure?.start_on || today) && lastDay <= addDays(today, 365) && (cancelling ? target.id != null && confirmed && !!cancelReason.trim() : reasonOk))}
      onSubmit={submit}
    >
      <Field label="퇴원일(마지막 수강일)" required hint="선택한 날까지 재원합니다. 오늘을 포함한 이후 날짜는 퇴원 예정입니다.">
        <DateInput value={lastDay} onChange={pickDate} min={tenure?.start_on || initialTenure?.start_on || today} max={addDays(today, 365)} disabled={refreshNeeded} />
      </Field>
      {completed.length > 0 && (
        <div role="status" className="rounded-xl bg-green-50 px-3 py-2 text-xs text-green-800">
          <p className="font-semibold">취소 완료 {completed.length}건</p>
          <ul className="mt-1 list-inside list-disc">{completed.map((item, i) => <li key={i}>{item}</li>)}</ul>
          <p className="mt-1">완료한 취소는 창을 닫아도 유지됩니다. 퇴원은 마지막 단계에서 따로 저장합니다.</p>
        </div>
      )}
      {refreshNeeded && <Note tone="warn">요청 결과와 남은 작업을 확인하려면 학생 정보를 다시 불러와 주세요.</Note>}
      {!canWithdraw && !refreshNeeded && <Note tone="warn">재원 상태가 바뀌었습니다. 창을 닫고 현재 상태를 확인해 주세요.</Note>}
      {canWithdraw && blocking.length > 0 && (
        <div className="space-y-2 rounded-xl border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900">
          <p className="font-semibold">퇴원 전에 확인할 기록 {blocking.length}건</p>
          <p>퇴원일 다음 날이나 그 뒤에 시작하는 기록입니다. 시작 전 취소할 기록만 한 건씩 처리합니다.</p>
          <ul className="list-inside list-disc space-y-1">
            {blocking.map((r, i) => <li key={`${r.kind}-${r.id}`} className={i === 0 ? 'font-semibold' : ''}>
              {r.label} · {r.start_on} ~ {(r.kind === 'enrollment' ? enrollmentLastDay(r) : r.end_on) || '진행 중'}{i === 0 ? ' — 이번 취소 대상' : ''}
            </li>)}
          </ul>
          {target.transfer_from_class_name && <p>이 입반을 취소하면 반 이동도 취소됩니다. {target.transfer_from_class_name} 편성이 이 기록의 종료일을 이어받습니다.</p>}
          <p>이미 수강하거나 휴원한 기록이면 날짜를 다시 확인해 주세요.</p>
        </div>
      )}
      {canWithdraw && cancelling && (
        <>
          <Field label="취소 사유" required>
            <TextInput value={cancelReason} onChange={setCancelReason} placeholder="예: 수강 시작 전 취소" disabled={refreshNeeded} />
          </Field>
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input type="checkbox" className={checkboxClass} checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={refreshNeeded} />
            <span>{`${target.label}의 ${target.start_on} 시작 기록이 시작 전 취소 대상임을 확인했습니다.`}</span>
          </label>
        </>
      )}
      {lastDay && (
        <Note>
          마지막 수강일 {md(lastDay)}
          {current.current.classes.filter((e) => e.start_on <= lastDay).map((e) => <span key={e.enrollment_id} className="block">{lastLine(e, addDays(lastDay, 1), byId)}</span>)}
        </Note>
      )}
      <>
          <Field label="퇴원 사유" required>
            <SelectInput value={reason} onChange={setReason} options={reasons.items.map((r) => [r.code, r.label])} placeholder="퇴원 사유 선택" disabled={refreshNeeded} />
            <EmptyCodesNote list={reasons} label="퇴원 사유" required />
          </Field>
          <Field label="메모"><TextArea value={memo} onChange={setMemo} rows={2} disabled={refreshNeeded} /></Field>
      </>
    </FormModal>
  );
}

function CancelWithdrawForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  return (
    <FormModal {...modal} submitLabel="퇴원 예정 취소" onSubmit={() => cancelWithdraw(detail.student_id, { ifRevision: detail.revision }, getAccessToken)}>
      <Note>
        {formatMonthDay(withdrawalDay(detail.current))} 퇴원 예정을 취소합니다. 반 편성도 복원됩니다.
      </Note>
    </FormModal>
  );
}

/**
 * 등록 · 재등록 — 「등록 전」 학생(학생 정보만 만든 학생 · v1 에만 있던 계정)도 같은 계정이 재원생이 된다(백엔드 2단계 전달 §1).
 * 첫 등록이면 서버가 학교·부서의 첫 기간을 등록일로 당긴다 — 재원인데 학교·학년이 비는 날이 없게(백엔드 2b 전달 §2).
 * 고른 반의 입반일은 등록일 — 반을 만든 날보다 앞이면 반을 만든 날부터. 한 반이라도 안 되면 등록도 안 된다(백엔드 4단계 전달 §3).
 */
function RegisterForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const { open } = useClassMaps();
  const [startOn, setStartOn] = useState(todayKST());
  const [classIds, setClassIds] = useState([]);
  const again = detail.tenures.length > 0;
  const lastEnd = detail.tenures.map((t) => t.end_on).filter(Boolean).sort().pop();
  return (
    <FormModal
      {...modal}
      submitLabel={again ? '재등록 저장' : '등록 저장'}
      canSubmit={!!startOn}
      onSubmit={() => registerStudent(detail.student_id, { startOn, classIds, ifRevision: detail.revision }, getAccessToken)}
    >
      <Field
        label="등록일"
        required
        hint={again
          ? `지난 퇴원일${lastEnd ? `(${lastDayOf(lastEnd)})` : ''} 다음 날부터`
          : '오늘 이후는 등록 예정'}
      >
        <DateInput value={startOn} onChange={setStartOn} min={lastEnd || undefined} />
      </Field>
      <Field label="함께 입반할 반" hint="여러 반을 선택할 수 있습니다.">
        <MultiSelectInput
          aria-label="함께 입반할 반"
          value={classIds}
          onChange={setClassIds}
          options={open.map((c) => [c.class_id, `${c.name}${c.teacher?.name ? ` · ${c.teacher.name}` : ''}`])}
          placeholder="반 선택"
          emptyText="활성 반이 없습니다."
        />
      </Field>
    </FormModal>
  );
}

function EnrollForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const { open } = useClassMaps();
  const mine = new Set(detail.current.classes.map((c) => c.class_id));
  const [classId, setClassId] = useState('');
  const [startOn, setStartOn] = useState(todayKST());
  return (
    <FormModal
      {...modal}
      submitLabel="입반 저장"
      canSubmit={!!classId && !!startOn}
      onSubmit={() => enrollStudent(detail.student_id, { classId: Number(classId), startOn, ifRevision: detail.revision }, getAccessToken)}
    >
      <Field label="반" required>
        <SelectInput value={classId} onChange={setClassId} options={open.filter((c) => !mine.has(c.class_id)).map((c) => [c.class_id, `${c.name} · ${c.teacher?.name || '담임 없음'}`])} placeholder="반 선택" />
      </Field>
      <Field label="입반일" required>
        <DateInput value={startOn} onChange={setStartOn} />
      </Field>
    </FormModal>
  );
}

function TransferForm({ detail, preset, ...modal }) {
  const { getAccessToken } = useAuth();
  const { open, byId } = useClassMaps();
  const current = movableClasses(detail);
  const [fromId, setFromId] = useState(String(preset?.enrollment_id ?? (current.length === 1 ? current[0].enrollment_id : '')));
  const [toId, setToId] = useState('');
  const [on, setOn] = useState(todayKST());
  const from = current.find((e) => String(e.enrollment_id) === String(fromId));
  return (
    <FormModal
      {...modal}
      submitLabel="반 이동 저장"
      canSubmit={!!from && !!toId && !!on}
      onSubmit={() => transferStudent(detail.student_id, { fromEnrollmentId: from.enrollment_id, toClassId: Number(toId), on, ifRevision: detail.revision }, getAccessToken)}
    >
      <Field label="옮길 반" required>
        <SelectInput value={fromId} onChange={setFromId} options={current.map((e) => [e.enrollment_id, `${e.class_name} · ${e.teacher?.name || '담임 없음'}`])} placeholder="옮길 반 선택" disabled={current.length <= 1} />
      </Field>
      <Field label="새 반" required>
        <SelectInput value={toId} onChange={setToId} options={open.filter((c) => !detail.current.classes.some((e) => e.class_id === c.class_id)).map((c) => [c.class_id, `${c.name} · ${c.teacher?.name || '담임 없음'}`])} placeholder="새 반 선택" />
      </Field>
      <Field label="이동일 — 새 반의 첫날" required>
        {/* 퇴원 예정으로 닫힌 편성은 마지막 수강일까지만(백엔드 4단계 전달 §3 반 이동) */}
        <DateInput value={on} onChange={setOn} min={from ? addDays(from.start_on, 1) : undefined} max={from?.end_on ? addDays(from.end_on, -1) : undefined} />
      </Field>
      {from && on && <Note>{lastLine(from, on, byId)}</Note>}
    </FormModal>
  );
}

function EndEnrollmentForm({ detail, preset, ...modal }) {
  const { getAccessToken } = useAuth();
  const reasons = useCodeList('class_leave_reason');
  const { byId } = useClassMaps();
  const current = movableClasses(detail);
  const [id, setId] = useState(String(preset?.enrollment_id ?? preset?.id ?? (current.length === 1 ? current[0].enrollment_id : '')));
  const [lastDay, setLastDay] = useState('');
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState('');
  const target = current.find((e) => String(e.enrollment_id) === String(id))
    || (preset ? { enrollment_id: preset.id, class_id: preset.class_id, class_name: preset.class_name, start_on: preset.start_on, end_on: preset.end_on, auto_closed: preset.auto_closed } : null);
  const maxDay = target?.end_on ? addDays(target.end_on, -2) : addDays(todayKST(), 365);
  return (
    <FormModal
      {...modal}
      danger
      submitLabel="퇴반 저장"
      canSubmit={!!target && !!lastDay && lastDay >= target.start_on && lastDay <= maxDay}
      onSubmit={() => endEnrollment(target.enrollment_id, { lastDay, reasonCode: reason, memo, ifRevision: detail.revision }, getAccessToken)}
    >
      <Field label="반" required>
        <SelectInput value={id} onChange={setId} options={current.map((e) => [e.enrollment_id, e.class_name])} placeholder="퇴반할 반 선택" disabled={current.length <= 1 && !!target} />
      </Field>
      <Field label="퇴반일(마지막 수강일)" required>
        {/* 퇴원일까지 다니는 반은 자동 종료된다. 별도 퇴반은 그 전날까지. */}
        <DateInput value={lastDay} onChange={setLastDay} min={target?.start_on} max={maxDay} />
      </Field>
      {target && lastDay && <Note>{lastLine(target, addDays(lastDay, 1), byId)}</Note>}
      {target?.end_on && <Note>퇴원일까지 수강하면 반 편성은 자동으로 끝납니다. 별도 퇴반은 {maxDay}까지 선택합니다.</Note>}
      <Field label="퇴반 사유">
        <SelectInput value={reason} onChange={setReason} options={reasons.items.map((r) => [r.code, r.label])} placeholder="퇴반 사유 미지정" />
        <EmptyCodesNote list={reasons} label="퇴반 사유" />
      </Field>
      {/* 퇴원 · 휴원 창처럼 메모 칸 — 「기타」를 고르면 자세한 내용은 여기에(백엔드 1단계 전달 §4-E) */}
      <Field label="메모"><TextArea value={memo} onChange={setMemo} rows={2} /></Field>
    </FormModal>
  );
}

const SCHOOL_MODES = [['correct', '입력값 수정'], ['change', '전학·진급 기록 추가']];

/** 학교 연필은 입력값 수정부터 — 등록 예정이면 상세에 보이는 등록일 기준 기록을 고른다. */
function SchoolForm({ detail, top, ...modal }) {
  const rows = [...detail.school_periods].sort((a, b) => String(b.start_on).localeCompare(String(a.start_on)));
  const [mode, setMode] = useState(rows.length ? 'correct' : 'change');
  const asOf = detail.as_of || todayKST();
  const nextTenure = detail.current.state === 'register_scheduled'
    ? [...detail.tenures].filter((t) => t.start_on > asOf).sort((a, b) => a.start_on.localeCompare(b.start_on))[0]
    : null;
  const preset = rows.find((p) => isActiveOn(p, nextTenure?.start_on || asOf));
  const modeTabs = (
    <div className="space-y-2">
      {top}
      {rows.length > 0 && <div className="flex"><SegTabs tabs={SCHOOL_MODES} value={mode} onChange={setMode} ariaLabel="학교·학년 저장 방식" /></div>}
    </div>
  );
  return mode === 'correct'
    ? <CorrectForm {...modal} key="correct" detail={detail} kind="school" rows={rows} preset={preset} top={modeTabs} submitLabel="학교·학년 저장" />
    : <NewSchoolPeriodForm {...modal} key="change" detail={detail} top={modeTabs} />;
}

/** 전학·진급은 기존 기록을 덮지 않고 새 기간을 연다. */
function NewSchoolPeriodForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const schools = useSchools();
  const c = detail.current;
  const [schoolId, setSchoolId] = useState(c.school?.school_id ? String(c.school.school_id) : '');
  const [grade, setGrade] = useState(c.grade ? String(c.grade) : '');
  const [startOn, setStartOn] = useState(todayKST());
  const blocking = detail.school_periods.filter((p) => startOn && p.start_on >= startOn);
  const selectedSchool = schools.find((s) => String(s.school_id) === schoolId) || (String(c.school?.school_id) === schoolId ? c.school : null);
  const pickSchool = (id) => {
    setSchoolId(id);
    const options = gradeOptionsForSchool(schools.find((s) => String(s.school_id) === id));
    setGrade((prev) => options.some(([value]) => value === prev) ? prev : '');
  };
  return (
    <FormModal
      {...modal}
      submitLabel="학교·학년 저장"
      canSubmit={!!grade && !!startOn && blocking.length === 0}
      onSubmit={() => changeSchool(detail.student_id, { schoolId: schoolId ? Number(schoolId) : null, grade: Number(grade), startOn, ifRevision: detail.revision }, getAccessToken)}
    >
      <Note>{detail.school_periods.length ? '전학하거나 학년이 바뀐 날부터 새 기록을 추가합니다.' : '학교·학년 기록을 처음 저장합니다.'}</Note>
      <Field label="학교">
        <SelectInput value={schoolId} onChange={pickSchool} options={schoolOptions(schools)} placeholder="학교 미지정" />
      </Field>
      {/* 학년은 자동으로 오르지 않는다 — 이 기간 내내 고른 학년 그대로(오너 결정 2026-10-12, 백엔드 0단계 전달 §3-1) */}
      <Field label="학년" required>
        <SelectInput value={grade} onChange={setGrade} options={gradeOptionsForSchool(selectedSchool, grade)} placeholder="학년 선택" />
      </Field>
      <Field label="시작일" required><DateInput value={startOn} onChange={setStartOn} /></Field>
      {blocking.length > 0 && (
        <Note tone="warn">
          선택한 시작일({startOn})과 같거나 더 늦게 시작하는 기록이 있습니다. 기존 기록 시작일: {blocking.map((p) => p.start_on).sort().join(', ')}.
          {' '}잘못 입력한 값은 위의 [입력값 수정]에서 고칩니다.
        </Note>
      )}
    </FormModal>
  );
}

function DepartmentForm({ detail, ...modal }) {
  const { getAccessToken } = useAuth();
  const depts = useAsync((signal) => listDepartments({ signal }, getAccessToken), 'depts-form');
  const [deptId, setDeptId] = useState('');
  const [startOn, setStartOn] = useState(todayKST());
  return (
    <FormModal
      {...modal}
      submitLabel="부서 저장"
      canSubmit={!!deptId && !!startOn}
      onSubmit={() => changeDepartment(detail.student_id, { departmentId: Number(deptId), startOn, ifRevision: detail.revision }, getAccessToken)}
    >
      <Field label="부서" required>
        <SelectInput value={deptId} onChange={setDeptId} options={(depts.data || []).filter((d) => d.department_id !== detail.current.department?.department_id).map((d) => [d.department_id, d.name])} placeholder="부서 선택" />
      </Field>
      <Field label="시작일" required><DateInput value={startOn} onChange={setStartOn} /></Field>
    </FormModal>
  );
}

const KIND_LABEL = { tenure: '재원 기간', pause: '휴원 기간', enrollment: '반 편성', school: '학교·학년 기간', department: '부서 기간' };
const rowLabel = (kind, r) => `${kind === 'tenure' && r.seq ? `${r.seq}차 · ` : ''}${r.class_name ? `${r.class_name} · ` : ''}${r.school?.name ? `${r.school.name} · ` : ''}${r.department?.name ? `${r.department.name} · ` : ''}${r.start_on} ~ ${(kind === 'tenure' ? tenureLastDay(r) : kind === 'enrollment' ? enrollmentLastDay(r) : r.end_on) || ''}`;

/**
 * 정정 창의 부서 고르기 — 감추지 않은 부서 + 그 줄의 지금 부서(감춘 부서여도). 서버는 지금 값을 그대로 다시 받는다
 * (감춘 부서의 지난 기록 정정 — 백엔드 2단계 전달 §3 · §4-I). 다른 감춘 부서는 고를 수 없다(서버 404).
 */
export function correctDeptOptions(depts, row) {
  const currentId = row?.department?.department_id;
  const options = depts
    .filter((d) => !d.archived || d.department_id === currentId)
    .map((d) => [d.department_id, d.archived ? `${d.name} (감춤)` : d.name]);
  // 목록을 못 받았거나 목록에 없으면 그 줄의 값을 그대로 둔다 — 고르기가 빈 칸으로 보이지 않게
  if (currentId != null && !options.some(([id]) => id === currentId)) options.unshift([currentId, row.department.name || String(currentId)]);
  return options;
}

/**
 * 정정 창의 종료일 첫 값 — 퇴원 예정일에 함께 닫힌 휴원(auto_closed)은 비워 둔다. 그 끝은 복귀일이 아니라 퇴원일이고,
 * 휴원 날짜는 오늘까지라(백엔드 5단계 전달 §4-E) 미래 퇴원일을 채워 두면 저장이 막힌다. 비워 두면 end_on 을 보내지 않아 그대로 둔다.
 */
const correctEndOf = (kind, r) => (kind === 'tenure' ? tenureLastDay(r) || '' : kind === 'enrollment' ? enrollmentLastDay(r) || '' : kind === 'pause' && r?.auto_closed ? '' : r?.end_on || '');

/** 기간 정정 — 잘못 적은 날짜·반·학교를 바로잡는다. 사유 필수(요청서 V11), 변경 기록에 남음 */
function CorrectForm({ detail, kind, rows, preset, submitLabel = '정정 저장', ...modal }) {
  const { getAccessToken } = useAuth();
  const schools = useSchools();
  const { all } = useClassMaps(kind === 'enrollment');
  const depts = useAsync((signal) => (kind === 'department' ? listDepartments({ includeArchived: true, signal }, getAccessToken) : Promise.resolve([])), `depts-correct-${kind}`);
  const list = rows || [];
  const [rowId, setRowId] = useState(String(preset?.id ?? (list.length === 1 ? list[0].id : '')));
  const row = list.find((r) => String(r.id) === rowId) || (String(preset?.id) === rowId ? preset : null);
  const [startOn, setStartOn] = useState(row?.start_on || '');
  const [endOn, setEndOn] = useState(correctEndOf(kind, row));
  const [classId, setClassId] = useState(row?.class_id ? String(row.class_id) : '');
  const [schoolId, setSchoolId] = useState(row?.school?.school_id ? String(row.school.school_id) : '');
  const [grade, setGrade] = useState(row?.grade_at_start ? String(row.grade_at_start) : '');
  const [deptId, setDeptId] = useState(row?.department?.department_id ? String(row.department.department_id) : '');
  const [reason, setReason] = useState(kind === 'school' ? '입력 실수' : '');
  const selectedSchool = schools.find((s) => String(s.school_id) === schoolId) || (String(row?.school?.school_id) === schoolId ? row.school : null);
  const pickSchool = (id) => {
    setSchoolId(id);
    const options = gradeOptionsForSchool(schools.find((s) => String(s.school_id) === id));
    setGrade((prev) => options.some(([value]) => value === prev) ? prev : '');
  };
  const pick = (id) => {
    setRowId(id);
    const r = list.find((x) => String(x.id) === id);
    setStartOn(r?.start_on || ''); setEndOn(correctEndOf(kind, r)); setClassId(r?.class_id ? String(r.class_id) : '');
    setSchoolId(r?.school?.school_id ? String(r.school.school_id) : ''); setGrade(r?.grade_at_start ? String(r.grade_at_start) : '');
    setDeptId(r?.department?.department_id ? String(r.department.department_id) : '');
  };
  // 휴원 날짜는 오늘까지(예정 없음 — 백엔드 5단계 전달 §4-E)
  const inclusive = kind === 'tenure' || kind === 'enrollment';
  const dateMax = kind === 'pause' ? todayKST() : inclusive ? addDays(todayKST(), 365) : undefined;
  const endMax = kind === 'enrollment' && row?.auto_closed ? enrollmentLastDay(row) : dateMax;
  const endHint = kind === 'pause' && row?.auto_closed && !endOn
    ? `비워 두면 퇴원 예정일(${formatMonthDay(lastDayOf(row.end_on))})에 함께 닫힌 그대로`
    : '비우면 진행 중';
  const schoolChanged = kind === 'school' && row && (
    startOn !== row.start_on || (endOn || null) !== row.end_on
    || (schoolId ? Number(schoolId) : null) !== (row.school?.school_id ?? null)
    || Number(grade) !== row.grade_at_start
  );
  const fields = () => {
    const f = { start_on: startOn, [inclusive ? 'last_day' : 'end_on']: endOn || null };
    // 퇴원일에 함께 닫힌 휴원의 종료일을 비워 두면 보내지 않는다 — 서버가 지금 끝(퇴원일)을 그대로 둔다
    if (kind === 'pause' && row?.auto_closed && !endOn) delete f.end_on;
    if (kind === 'enrollment' && classId) f.class_id = Number(classId);
    if (kind === 'school') { f.school_id = schoolId ? Number(schoolId) : null; f.grade_at_start = Number(grade); }
    if (kind === 'department' && deptId) f.department_id = Number(deptId);
    return f;
  };
  return (
    <FormModal
      {...modal}
      submitLabel={submitLabel}
      canSubmit={!!row && !!startOn && (!endOn || (inclusive ? endOn >= startOn : endOn > startOn)) && (!endOn || !endMax || endOn <= endMax) && !!reason.trim() && (kind !== 'school' || (!!grade && schoolChanged))}
      onSubmit={() => correctPeriod(kind, row.id, fields(), { reason, ifRevision: detail.revision }, getAccessToken)}
    >
      {kind === 'school' && <Note>선택한 기존 기록의 학교·학년과 날짜를 고칩니다.</Note>}
      {list.length > 1 && (
        <Field label={`수정할 ${KIND_LABEL[kind]}`} required>
          <SelectInput value={rowId} onChange={pick} options={list.map((r) => [r.id, rowLabel(kind, r)])} placeholder={`${KIND_LABEL[kind]} 선택`} />
        </Field>
      )}
      {kind === 'enrollment' && <Field label="반"><SelectInput value={classId} onChange={setClassId} options={all.map((c) => [c.class_id, c.name])} /></Field>}
      {kind === 'school' && (
        <>
          <Field label="학교"><SelectInput value={schoolId} onChange={pickSchool} options={schoolOptions(schools)} placeholder="학교 미지정" /></Field>
          <Field label="학년" required><SelectInput value={grade} onChange={setGrade} options={gradeOptionsForSchool(selectedSchool, grade)} placeholder="학년 선택" /></Field>
        </>
      )}
      {kind === 'department' && <Field label="부서"><SelectInput value={deptId} onChange={setDeptId} options={correctDeptOptions(depts.data || [], row)} /></Field>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="시작일" required><DateInput value={startOn} onChange={setStartOn} max={dateMax} /></Field>
        <Field label={kind === 'tenure' ? '퇴원일(마지막 수강일)' : kind === 'enrollment' ? '퇴반일(마지막 수강일)' : '종료일 — 아닌 첫날'} hint={endHint}><DateInput value={endOn} onChange={setEndOn} min={startOn ? inclusive ? startOn : addDays(startOn, 1) : undefined} max={endMax} /></Field>
      </div>
      {kind === 'pause' && <Note>휴원 날짜는 오늘까지만 넣습니다. 이미 처리한 반 퇴반·입반 날짜는 함께 바뀌지 않습니다. 반 편성 탭에서 확인해 주세요.</Note>}
      <Field label="정정 사유" required><TextInput value={reason} onChange={setReason} placeholder="예: 입력 실수" /></Field>
    </FormModal>
  );
}

/**
 * 기간 삭제 — 잘못 만든 기록을 지운다(삭제 표시). 재원 기간을 지우면 그 안의 편성·휴원도 함께(요청서 V12 — 서버 연쇄는 4·5단계).
 * rows 를 주면(재원 기간 삭제) 지울 줄을 고른다 — 둘 이상이면 미리 고르지 않는다(지우는 작업이라).
 */
function DeleteForm({ detail, kind, preset, rows, ...modal }) {
  const { getAccessToken } = useAuth();
  const list = rows || (preset ? [preset] : []);
  const [rowId, setRowId] = useState(String(preset?.id ?? (list.length === 1 ? list[0].id : '')));
  const row = list.find((r) => String(r.id) === rowId) || null;
  const [reason, setReason] = useState('');
  return (
    <FormModal
      {...modal}
      danger
      submitLabel="삭제"
      canSubmit={!!row && !!reason.trim()}
      onSubmit={() => deletePeriod(kind, row.id, { reason, ifRevision: detail.revision }, getAccessToken)}
    >
      {list.length > 1 && (
        <Field label={`지울 ${KIND_LABEL[kind]}`} required>
          <SelectInput value={rowId} onChange={setRowId} options={list.map((r) => [r.id, rowLabel(kind, r)])} placeholder={`${KIND_LABEL[kind]} 선택`} />
        </Field>
      )}
      {row && (
        <Note tone="warn">
          {KIND_LABEL[kind]} {rowLabel(kind, row)} 을(를) 삭제합니다.
          {kind === 'tenure' && ' 반 편성·휴원 기록도 함께 삭제됩니다.'}
          {kind === 'pause' && ' 반 편성은 자동으로 복원되지 않습니다. 삭제 후 반 편성 탭에서 확인해 주세요.'}
          {/* 반 이동으로 들어온 편성을 지우면 반 이동 취소 — 앞 반 편성이 이 줄의 끝을 이어받는다(백엔드 4단계 전달 §3 · §4-C) */}
          {kind === 'enrollment' && row.transfer_from_class_name && (
            <span className="block">지우면 반 이동이 취소되고 {row.transfer_from_class_name} 편성이 이 줄의 끝을 이어받습니다.</span>
          )}
        </Note>
      )}
      <Field label="삭제 사유" required><TextInput value={reason} onChange={setReason} placeholder="예: 잘못 만든 기록" /></Field>
    </FormModal>
  );
}

/**
 * 학생 정보 수정(S4 — 백엔드 2단계 전달 §4-B) — 바꾼 칸만 보낸다. 비밀번호는 적었을 때만.
 * 원래 값과 같으면 보내지 않는다(서버도 바뀐 칸만 검사 — 옛 계정의 표기가 지금 규칙에 맞지 않아도 다른 칸은 수정할 수 있게).
 * 돌려주는 값은 updateProfile 의 인자 모양(camelCase). 출결번호를 비우면 null(지우기).
 */
export function profileChanges(profile, form) {
  const out = {};
  const name = form.name.trim();
  const email = form.email.trim();
  const no = form.studentNo.trim();
  if (name !== profile.student_name) out.studentName = name;
  if (email.toLowerCase() !== (profile.student_email || '').toLowerCase()) out.studentEmail = email;
  if (form.password) out.password = form.password;
  if (no !== (profile.student_no || '')) out.studentNo = no || null;
  if (form.mobile.trim() !== profile.mobile_number) out.mobileNumber = form.mobile.trim();
  if (form.parent.trim() !== profile.parent_mobile_number) out.parentMobileNumber = form.parent.trim();
  if (typeof form.isTest === 'boolean' && form.isTest !== !!profile.is_test) out.isTest = form.isTest;
  return out;
}

function ProfileForm({ detail, ...modal }) {
  const { getAccessToken, userType } = useAuth();
  const p = detail.profile;
  const [f, setF] = useState({
    name: p.student_name, email: p.student_email, password: '', studentNo: p.student_no || '', mobile: p.mobile_number, parent: p.parent_mobile_number, isTest: p.is_test,
  });
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const changes = profileChanges(p, { ...f, isTest: userType === 'manager' ? f.isTest : undefined });
  return (
    <FormModal
      {...modal}
      submitLabel="정보 저장"
      canSubmit={!p.is_deleted && Object.keys(changes).length > 0 && !!f.name.trim() && !!f.email.trim() && studentNoOk(f.studentNo)}
      onSubmit={() => updateProfile(detail.student_id, changes, getAccessToken)}
    >
      {p.is_deleted && <Note tone="warn">삭제된 학생 계정이라 수정할 수 없습니다.</Note>}
      {p.is_blocked && <Note tone="warn">로그인 잠김 — 비밀번호를 새로 정하면 풀립니다.</Note>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="이름" required hint={NAME_RULE}><TextInput value={f.name} onChange={set('name')} autoComplete="off" /></Field>
        <Field label="로그인 이메일" required><TextInput value={f.email} onChange={set('email')} placeholder="로그인 이메일" inputMode="email" autoComplete="off" /></Field>
        <Field label="새 비밀번호" hint={PASSWORD_RULE}><PasswordInput value={f.password} onChange={set('password')} placeholder="변경할 때 입력" /></Field>
        <Field label="출결번호" hint={studentNoHint(f.studentNo)}><StudentNoInput value={f.studentNo} onChange={set('studentNo')} placeholder="없음" /></Field>
        <Field label="학생 연락처"><TextInput value={f.mobile} onChange={set('mobile')} placeholder="010-0000-0000(선택)" inputMode="tel" /></Field>
        <Field label="학부모 연락처"><TextInput value={f.parent} onChange={set('parent')} placeholder="010-0000-0000(선택)" inputMode="tel" /></Field>
      </div>
      {changes.studentEmail !== undefined && (
        <Note tone="warn">학생은 새 이메일로 다시 로그인해야 합니다. 바꿔 나간 이메일은 하루 동안 다른 학생이 쓸 수 없습니다.</Note>
      )}
      {userType === 'manager' && <TestStudentField checked={!!f.isTest} onChange={set('isTest')} disabled={p.is_deleted} />}
    </FormModal>
  );
}

const FORMS = {
  withdraw: (p) => <WithdrawForm {...p} />,
  changeWithdraw: (p) => <WithdrawForm {...p} change />,
  cancelWithdraw: (p) => <CancelWithdrawForm {...p} />,
  pause: (p) => <PauseForm {...p} />,
  pauseClasses: (p) => <PauseForm {...p} />,
  resume: (p) => <ResumeForm {...p} />,
  register: (p) => <RegisterForm {...p} />,
  registerFirst: (p) => <RegisterForm {...p} />,
  enroll: (p) => <EnrollForm {...p} />,
  transfer: (p) => <TransferForm {...p} />,
  endEnrollment: (p) => <EndEnrollmentForm {...p} />,
  school: (p) => <SchoolForm {...p} />,
  department: (p) => <DepartmentForm {...p} />,
  correctTenure: (p) => <CorrectForm {...p} kind="tenure" rows={p.detail.tenures} />,
  deleteTenure: (p) => <DeleteForm {...p} kind="tenure" rows={p.detail.tenures} />,
  correctPause: (p) => <CorrectForm {...p} kind="pause" rows={p.detail.pauses} />,
  deletePause: (p) => <DeleteForm {...p} kind="pause" rows={p.detail.pauses} />,
  correct: (p) => <CorrectForm {...p} kind={p.kind} rows={p.rows || (p.preset ? [p.preset] : [])} />,
  delete: (p) => <DeleteForm {...p} kind={p.kind} />,
  profile: (p) => <ProfileForm {...p} />,
};

/**
 * 학생 작업 창. actions 가 둘 이상이면 창 머리에 작업 고르기 단추가 서고, 고른 작업의 칸이 아래에 선다.
 * kind · preset · rows — 정정·삭제·퇴반·반 이동처럼 특정 줄에서 연 경우.
 */
function StudentActionModal({ detail, actions, initial, kind, preset, rows, onClose }) {
  const [action, setAction] = useState(initial || actions[0]);
  const top = actions.length > 1 ? (
    <div className="flex flex-wrap">
      <SegTabs tabs={actions.map((a) => [a, ACTION_LABEL[a]])} value={action} onChange={setAction} ariaLabel="작업 선택" />
    </div>
  ) : null;
  const title = `${detail.student_name} — ${action === 'correct' || action === 'delete' ? `${KIND_LABEL[kind]} ${ACTION_LABEL[action]}` : ACTION_LABEL[action]}`;
  const render = FORMS[action];
  // 작업을 바꾸면 칸을 새로 세운다(앞 작업의 입력이 남지 않게)
  return <React.Fragment key={action}>{render({ detail, kind, preset, rows, onClose, title, top, fixedHeight: actions.length > 1, lockWhileBusy: actions.length > 1 })}</React.Fragment>;
}

export default StudentActionModal;
